import { axiosService } from '@/services/AxiosService';
import PermissionService from './PermissionService';
import { useToast } from 'vue-toastification';

/**
 * @typedef {Object} Member
 * @property {number} id
 * @property {string} username
 * @property {number} role_id
 * @property {string|null} first_name
 * @property {string|null} last_name
 * @property {string|null} location
 * @property {string|null} profession
 * @property {string|null} phone_number
 * @property {string|null} photo_url
 * @property {string|null} email
 * @property {boolean} deleted
 * @property {string|null} deleted_data
 * @property {string} created
 * @property {string} modified
 * @property {RidingStable[]} riding_stables
 */

/**
 * @typedef {Object} RidingStable
 * @property {number} id
 * @property {string} name
 * @property {string} address
 * @property {number} fiscal_data_id
 * @property {string} created
 * @property {string} modified
 * @property {RidingStableJoinData} _joinData
 */

/**
 * @typedef {Object} RidingStableJoinData
 * @property {number} user_id
 * @property {number} riding_stable_id
 */

class MemberService {
	getPath = '/members/get?riding_stable_id=';
	postPath = '/members/save';
	delPath = '/members/delete';
	toast = useToast();
	members = [];

	constructor() {
		this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
	}

	/**
	 * Ottieni la lista dei soci per il riding stable.
	 * @returns {Promise<Member[]>} I dati dei soci.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async getMembers(page, search = '', params = {}, length = 10) {
		if (!PermissionService.ROLE_STABLE_MANAGER == params.role_id) return;
		try {
			const response = await this.axiosInstance.get(
				this.getPath + params.riding_stable_id,
				{ page, length, search }
			);

			if (response.status !== 200) {
				throw new Error('Failed to fetch members info');
			}

			/** @type {Member[]} */
			// console.log('membersResponse ---> ', response);
			const membersData = { data: response.data.members };
			this.members = response.data.members;
			return membersData;
		} catch (error) {
			console.error('Error fetching members info:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}

	getMemberById(member_id) {
		const result = this.members.find((member) => member.id === member_id);
		return result;
	}

	/**
	 * Crea/modifica socio per il riding stable.
	 * @returns {Promise<string>} Messaggio di conferma con ID.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async saveMember(riding_stable_id, role_id, member) {
		if (!PermissionService.ROLE_STABLE_MANAGER == role_id) return;
		try {
			const userMember = {
				password: member.password,
				first_name: member.first_name,
				last_name: member.last_name,
				location: member.location,
				profession: member.profession,
				phone_number: member.phone_number,
				photo_url: member.photo_url,
				email: member.email,
				riding_stable_id: riding_stable_id,
			};
			if (member.id) {
				userMember.id = member.id;
			} else {
				userMember.username = member.username;
			}

			const response = await this.axiosInstance.post(this.postPath, userMember);

			if (response.status !== 200) {
				throw new Error('Failed to create member');
			}

			/** @type {string} */
			// console.log('messageResponse ---> ', response);
			// const messageResponse = response.data.message;
			// this.toast.success(messageResponse);
			return response;
		} catch (error) {
			this.toast.error(error);
			console.error('Error adding member:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}

	/**
	 * Elimina socio per il riding stable.
	 * @returns {Promise<string>} Messaggio di conferma.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async deleteMember(riding_stable_id, role_id, member_id) {
		if (!PermissionService.ROLE_STABLE_MANAGER == role_id) return;
		try {
			const response = await this.axiosInstance.delete(this.delPath, {
				data: {
					id: member_id,
					riding_stable_id: riding_stable_id,
				},
			});

			if (response.status !== 200) {
				throw new Error('Failed to delete member');
			}

			/** @type {string} */
			// console.log('messageResponse ---> ', response);
			const messageResponse = response.data.message;
			this.toast.success(messageResponse);
			return messageResponse;
		} catch (error) {
			this.toast.error(error);
			console.error('Error deleting member:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}
}

export const membersService = new MemberService();
